import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { tr } from 'date-fns/locale/tr';
import { format, addDays } from 'date-fns';
import { Service, TimeSlot, TeamMember } from '../../types/salonTypes';
import { generateTimeSlots } from '../../data/salonData';
import apiService from '../../utils/api';

// Turkish date picker localization
registerLocale('tr', tr);

interface AppointmentFormProps {
  salonId?: string;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ salonId }) => {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [formStep, setFormStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [staff, setStaff] = useState<TeamMember[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [salonSlug, setSalonSlug] = useState<string>('');
  const [salonName, setSalonName] = useState<string>('');

  // SMS doğrulama ile ilgili state'ler
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [verificationSent, setVerificationSent] = useState<boolean>(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState<boolean>(false);

  // Salon ve Personel çalışma günleri
  const [salonWorkingDays, setSalonWorkingDays] = useState<any>({});
  const [staffWorkingDays, setStaffWorkingDays] = useState<any>({});
  const [currentStaffId, setCurrentStaffId] = useState<string | null>(null);

  // Salon ID'yi loglama
  useEffect(() => {
    if (salonId) {
      console.log('Randevu formu için salon ID:', salonId);
      fetchSalonInfo();
      fetchServices();
    }
  }, [salonId]);

  // Salon bilgilerini getir
  const fetchSalonInfo = async () => {
    if (!salonId) return;
    
    try {
      // Önce salon bilgilerini al
      const salonResponse = await apiService.getSalonInfo(salonId);
      if (salonResponse.success && salonResponse.salon) {
        setSalonName(salonResponse.salon.name || '');
      }
      
      // Sonra website bilgilerini al (slug içerir)
      const websiteResponse = await apiService.getWebsiteContent(salonId);
      
      if (websiteResponse.success && websiteResponse.websiteContent) {
        const slug = websiteResponse.websiteContent.slug;
        if (slug) {
          setSalonSlug(slug);
          console.log('Website bilgileri yüklendi, slug:', slug);
        } else {
          // Slug yoksa salon adından oluştur
          generateSlugFromSalonName(salonResponse.salon?.name || '');
        }
      } else {
        // Website bilgisi yoksa salon adından slug oluştur
        generateSlugFromSalonName(salonResponse.salon?.name || '');
      }
    } catch (err) {
      console.error('Salon/Website bilgileri yüklenirken hata:', err);
    }
  };
  
  // Salon adından slug oluşturma
  const generateSlugFromSalonName = (name: string) => {
    if (!name) return;
    
    // Türkçe karakterleri İngilizce karakterlere dönüştür
    const turkishToEnglish: {[key: string]: string} = {
      'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
      'Ç': 'C', 'Ğ': 'G', 'İ': 'I', 'Ö': 'O', 'Ş': 'S', 'Ü': 'U'
    };
    
    // Slug oluştur
    const slug = name
      .toLowerCase()
      .replace(/[çğıöşüÇĞİÖŞÜ]/g, letter => turkishToEnglish[letter] || letter)
      .replace(/[^a-z0-9]+/g, '-')  // alfanümerik olmayan karakterleri tire ile değiştir
      .replace(/^-+|-+$/g, '');     // baştaki ve sondaki tireleri kaldır
    
    setSalonSlug(slug);
    console.log('Salon adından slug oluşturuldu:', slug);
  };

  // Resend timer için interval
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  // Servisleri API'den çek
  const fetchServices = async () => {
    if (!salonId) {
      setError('Salon kimliği bulunamadı');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiService.getSalonServices(salonId);
      
      if (response.success && response.services) {
        console.log('Salon hizmetleri yüklendi:', response.services);

        // Backend'den dönen servisleri frontend modelimize dönüştür
        const mappedServices = response.services.map((service: any) => ({
          id: service._id,
          name: service.name,
          description: service.description || '',
          price: service.price,
          duration: service.duration,
          image: service.image || '/images/services/default.jpg'
        }));

        setServices(mappedServices);
        
        // Personel bilgilerini API'den getir
        fetchStaff();
      } else {
        setError('Hizmetler alınamadı');
        setLoading(false);
      }
    } catch (err) {
      console.error('Hizmetler yüklenirken hata:', err);
      setError('Hizmetler yüklenirken bir hata oluştu');
      setLoading(false);
    }
  };

  // Personel bilgilerini API'den çek
  const fetchStaff = async () => {
    if (!salonId) {
      setError('Salon kimliği bulunamadı');
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiService.getSalonStaff(salonId);
      
      if (response.success && response.staff) {
        console.log('Salon personelleri yüklendi:', response.staff);
        
        // Backend'den dönen personel verilerini frontend modelimize dönüştür
        const mappedStaff = response.staff.map((staffMember: any) => ({
          id: staffMember._id,
          name: staffMember.name,
          position: staffMember.role || 'Personel',
          bio: staffMember.bio || '',
          image: staffMember.image || '/images/team/default.jpg',
          specialties: staffMember.services 
            ? staffMember.services.map((s: any) => s.name || '')
            : []
        }));

        setStaff(mappedStaff);
      } else {
        console.error('Personel verileri alınamadı, statik veriler kullanılacak');
        // Statik personel verilerini varsayılan olarak ayarla
        setStaff([
          {
            id: 'staff1',
            name: 'Ayşe Yılmaz',
            position: 'Saç Stilisti',
            bio: 'Alanında 10 yıllık deneyime sahip uzman saç stilisti',
            image: '/images/team/staff1.jpg',
            specialties: ['Saç Kesimi', 'Saç Boyama', 'Fön']
          },
          {
            id: 'staff2',
            name: 'Mehmet Demir',
            position: 'Kuaför',
            bio: 'Erkek saç ve sakal tasarımında uzman',
            image: '/images/team/staff2.jpg',
            specialties: ['Saç Kesimi', 'Sakal Tıraşı']
          },
          {
            id: 'staff3',
            name: 'Zeynep Kaya',
            position: 'Manikür Uzmanı',
            bio: 'Tırnak sanatında uzmanlaşmış estetisyen',
            image: '/images/team/staff3.jpg',
            specialties: ['Manikür', 'Pedikür', 'Kalıcı Oje']
          },
          {
            id: 'staff4',
            name: 'Deniz Şahin',
            position: 'Cilt Bakım Uzmanı',
            bio: 'Medikal cilt bakımı konusunda sertifikalı uzman',
            image: '/images/team/staff4.jpg',
            specialties: ['Klasik Cilt Bakımı', 'Anti-Aging Bakım', 'Hydrafacial']
          },
          {
            id: 'staff5',
            name: 'Elif Yıldız',
            position: 'Makyöz',
            bio: 'Özel günler ve gelin makyajında uzman',
            image: '/images/team/staff5.jpg',
            specialties: ['Gündüz Makyajı', 'Gece Makyajı', 'Gelin Makyajı']
          }
        ]);
      }
    } catch (err) {
      console.error('Personel verileri yüklenirken hata:', err);
      // Statik personel verilerini varsayılan olarak ayarla
      setStaff([
        {
          id: 'staff1',
          name: 'Ayşe Yılmaz',
          position: 'Saç Stilisti',
          bio: 'Alanında 10 yıllık deneyime sahip uzman saç stilisti',
          image: '/images/team/staff1.jpg',
          specialties: ['Saç Kesimi', 'Saç Boyama', 'Fön']
        },
        {
          id: 'staff2',
          name: 'Mehmet Demir',
          position: 'Kuaför',
          bio: 'Erkek saç ve sakal tasarımında uzman',
          image: '/images/team/staff2.jpg',
          specialties: ['Saç Kesimi', 'Sakal Tıraşı']
        },
        {
          id: 'staff3',
          name: 'Zeynep Kaya',
          position: 'Manikür Uzmanı',
          bio: 'Tırnak sanatında uzmanlaşmış estetisyen',
          image: '/images/team/staff3.jpg',
          specialties: ['Manikür', 'Pedikür', 'Kalıcı Oje']
        },
        {
          id: 'staff4',
          name: 'Deniz Şahin',
          position: 'Cilt Bakım Uzmanı',
          bio: 'Medikal cilt bakımı konusunda sertifikalı uzman',
          image: '/images/team/staff4.jpg',
          specialties: ['Klasik Cilt Bakımı', 'Anti-Aging Bakım', 'Hydrafacial']
        },
        {
          id: 'staff5',
          name: 'Elif Yıldız',
          position: 'Makyöz',
          bio: 'Özel günler ve gelin makyajında uzman',
          image: '/images/team/staff5.jpg',
          specialties: ['Gündüz Makyajı', 'Gece Makyajı', 'Gelin Makyajı']
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Salon çalışma günlerini getir
  const fetchWorkingDays = async (staffId?: string) => {
    if (!salonId) return;
    
    try {
      const response = await apiService.getWorkingDays(salonId, staffId);
      
      if (response.success) {
        setSalonWorkingDays(response.salonWorkingDays || {});
        setStaffWorkingDays(response.staffWorkingDays || null);
      } else {
        console.error('Çalışma günleri alınamadı');
      }
    } catch (error) {
      console.error('Çalışma günleri yüklenirken hata:', error);
    }
  };

  // İlk yüklemede salon çalışma günlerini getir
  useEffect(() => {
    if (salonId) {
      fetchWorkingDays();
    }
  }, [salonId]);

  // Personel değiştiğinde personel çalışma günlerini getir API çağrısını yapacak fonksiyon
  const handleStaffChange = (staffId: string) => {
    if (staffId && staffId !== currentStaffId) {
      setCurrentStaffId(staffId);
      fetchWorkingDays(staffId);
      
      // Personel değiştiğinde tarih seçimini sıfırla
      setSelectedDate(null);
    }
  };

  // Seçili hizmetler değiştiğinde fiyat ve süreyi güncelle
  useEffect(() => {
    let price = 0;
    let duration = 0;
    
    selectedServices.forEach(serviceId => {
      const service = services.find(s => s.id === serviceId);
      if (service) {
        price += service.price;
        duration += service.duration;
      }
    });
    
    setTotalPrice(price);
    setTotalDuration(duration);

    // Formik değerlerine seçili hizmetleri ata
    formik.setFieldValue('services', selectedServices);
  }, [selectedServices, services]);

  // Hizmet seçildiğinde, personel seçimini sıfırla
  useEffect(() => {
    formik.setFieldValue('staff', '');
  }, [selectedServices]);

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices(prevSelected => {
      if (prevSelected.includes(serviceId)) {
        return prevSelected.filter(id => id !== serviceId);
      } else {
        return [...prevSelected, serviceId];
      }
    });
  };

  // Servis seçildiğinde ilgili personelleri filtreleme
  const getAvailableStaff = () => {
    if (selectedServices.length === 0) return [];
    
    // Seçilen hizmetlerden herhangi birini yapabilen personeli getir
    return staff.filter((member) => {
      return selectedServices.some(serviceId => {
        const service = services.find(s => s.id === serviceId);
        return service && member.specialties.includes(service.name);
      });
    });
  };

  // Tarih değiştiğinde çağrılacak fonksiyon
  const handleDateChange = (date: any) => {
    setSelectedDate(date);
    formik.setFieldValue('date', date);
    formik.setFieldValue('timeSlot', '');
    
    // Eğer salon ID ve tarih varsa, gerçek veri ile slot oluştur
    if (salonId && date && formik.values.staff) {
      fetchTimeSlots(salonId, formik.values.staff, date);
    } else if (date) {
      // Salon ID veya personel eksikse boş slotlar göster
      setTimeSlots([]);
      setError('Salon ID veya personel seçimi eksik, lütfen önce personel seçiniz.');
    }
  };

  // Zaman slotlarını getiren fonksiyon
  const fetchTimeSlots = async (salonId: string, staffId: string, date: Date) => {
    try {
      setLoading(true);
      setError('');
      // Asenkron slot oluşturma fonksiyonunu çağır
      const slots = await generateTimeSlots(salonId, staffId, date);
      
      if (slots.length === 0) {
        setError('Bu tarihte müsait zaman dilimi bulunamadı. Lütfen başka bir tarih seçiniz.');
      }
      
      setTimeSlots(slots);
    } catch (error) {
      console.error("Zaman slotları yüklenirken hata:", error);
      // Hata durumunda boş liste göster
      setTimeSlots([]);
      setError('Zaman slotları yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.');
    } finally {
      setLoading(false);
    }
  };

  // Tarih için kullanılabilirlik kontrolü - salon ve personel çalışma günlerine göre
  const isDateAvailable = (date: Date) => {
    // Hafta içi veya hafta sonu kontrolü artık yapmıyoruz, çalışma günlerine göre bakacağız
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const maxDate = addDays(today, 30);
    if (date < today || date > maxDate) return false;
    
    // Haftanın günü ismini al (0: Pazar, 1: Pazartesi, ..., 6: Cumartesi)
    const dayIndex = date.getDay();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = days[dayIndex];
    
    // Önce salon çalışma günlerini kontrol et
    if (!salonWorkingDays[dayName] || !salonWorkingDays[dayName].isOpen) {
      return false;
    }
    
    // Eğer personel seçilmişse ve personel çalışma günleri varsa kontrol et
    if (staffWorkingDays && currentStaffId) {
      return staffWorkingDays[dayName] && staffWorkingDays[dayName].isOpen;
    }
    
    // Sadece salon çalışma günlerine göre kontrol et
    return true;
  };

  // Tarih gösterimini özelleştirmek için
  const renderDayContents = (day: number, date: Date) => {
    const isAvailable = isDateAvailable(date);
    return (
      <span style={{ 
        color: isAvailable ? 'inherit' : '#ff4d4f',
        textDecoration: isAvailable ? 'none' : 'line-through',
        opacity: isAvailable ? 1 : 0.5
      }}>
        {day}
      </span>
    );
  };

  const validationSchema = Yup.object({
    services: Yup.array().min(1, 'En az bir hizmet seçmelisiniz'),
    staff: Yup.string().required('Lütfen bir personel seçin'),
    date: Yup.date().required('Lütfen bir tarih seçin'),
    timeSlot: Yup.string().required('Lütfen bir saat seçin'),
    name: Yup.string().required('İsim Soyisim gerekli'),
    email: Yup.string().email('Geçerli bir e-posta adresi girin').required('E-posta adresi gerekli'),
    phone: Yup.string()
      .matches(/^[0-9]{10,11}$/, 'Geçerli bir telefon numarası giriniz')
      .required('Telefon numarası gerekli'),
    notes: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      services: [] as string[],
      staff: '',
      date: null as Date | null,
      timeSlot: '',
      name: '',
      email: '',
      phone: '',
      notes: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (isVerificationModalOpen) {
        return; // Eğer doğrulama modalı açıksa, işlem yapmayı durdur
      }
      
      try {
        setSubmitting(true);
        setError(null); // Hata mesajını temizle
        
        // Seçilen tarih formatını ayarla
        const selectedTimeSlot = timeSlots.find(slot => slot.id === values.timeSlot);
        if (!selectedDate || !salonId || !selectedTimeSlot) {
          throw new Error('Gerekli randevu bilgileri eksik');
        }
        
        // Tarihi YYYY-MM-DD formatına çevir
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        // API'ye gönderilecek veriyi hazırla
        const appointmentData = {
          salonId,
          staffId: values.staff,
          services: values.services,
          customerName: values.name,
          customerEmail: values.email,
          customerPhone: values.phone,
          date: dateString,
          startTime: selectedTimeSlot.time,
          notes: values.notes,
          ...(verificationCode ? { verificationCode } : {}) // Eğer doğrulama kodu varsa ekle
        };
        
        // Randevu oluştur
        const response = await apiService.createAppointment(appointmentData);
        
        if (response.success) {
          // Başarılı
          console.log('Randevu başarıyla oluşturuldu:', response);
          setSubmitted(true);
          // Sayfa en üste scroll et
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (response.requireVerification) {
          // Doğrulama gerekli
          console.log('Telefon doğrulaması gerekiyor');
          
          // Geliştirme modunda doğrulama kodu alındıysa kullan
          if (response.verificationCode) {
            setVerificationCode(response.verificationCode);
            console.log('Doğrulama kodu (Dev mode):', response.verificationCode);
          }
          
          // Doğrulama modalını aç
          setVerificationSent(true);
          setIsVerificationModalOpen(true);
          
          // Yeniden gönderme için sayaç başlat
          setResendTimer(60);
        } else {
          // Diğer hatalar
          throw new Error(response.message || 'Randevu oluşturulurken bir hata oluştu');
        }
      } catch (error) {
        console.error('Randevu oluşturma hatası:', error);
        
        // Hata mesajı doğrulama gerektiriyorsa modalı aç
        const errorMessage = error instanceof Error ? error.message : 'Randevu oluşturulurken bir hata oluştu';
        
        if (errorMessage.includes('doğrulanmamış') || errorMessage.includes('doğrulama')) {
          // SMS doğrulama gerekiyor, modal açılmalı
          setVerificationSent(true);
          setIsVerificationModalOpen(true);
          setResendTimer(60);
        }
        
        setError(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // SMS doğrulama kodu gönderme
  const handleSendVerificationCode = async () => {
    if (!formik.values.phone || formik.errors.phone) {
      formik.setTouched({...formik.touched, phone: true});
      return;
    }
    
    setVerifying(true);
    setVerificationError(null);
    
    try {
      // Doğrudan SMS doğrulama servisi çağır
      const response = await apiService.sendVerificationCode(formik.values.phone);
      
      if (response.success) {
        console.log('[SMS] Doğrulama kodu gönderildi');
        
        // Geliştirme modunda doğrulama kodu alındıysa kullan
        if (response.verificationCode) {
          setVerificationCode(response.verificationCode);
          console.log('Doğrulama kodu (Dev mode):', response.verificationCode);
        } else {
          // Kullanıcı girişi için input'u temizle
          setVerificationCode('');
        }
        
        // Doğrulama gönderildi durumunu güncelle
        setVerificationSent(true);
        setResendTimer(60); // 60 saniye bekletme süresi
      } else {
        // Doğrulama kodu gönderme başarısız
        setVerificationError(response.message || 'SMS doğrulama kodu gönderme başarısız');
      }
    } catch (error) {
      console.error('SMS gönderme hatası:', error);
      setVerificationError('Doğrulama kodu gönderilirken bir hata oluştu');
    } finally {
      setVerifying(false);
    }
  };

  // SMS doğrulama kodunu doğrulama
  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setVerificationError('Lütfen geçerli bir doğrulama kodu girin');
      return;
    }
    
    setVerifying(true);
    setVerificationError(null);
    
    try {
      // Doğrulama kodu ile doğrudan randevu oluşturma isteği yapacağız
      // Bu nedenle verifyCode API çağrısına gerek yok
      setIsPhoneVerified(true);
      
      // Formik submit işlemini tekrar çağır - bu sefer verificationCode ile
      const selectedTimeSlot = timeSlots.find(slot => slot.id === formik.values.timeSlot);
      if (!selectedDate || !salonId || !selectedTimeSlot) {
        throw new Error('Gerekli randevu bilgileri eksik');
      }
      
      // Tarihi YYYY-MM-DD formatına çevir
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      
      // API'ye gönderilecek veriyi hazırla
      const appointmentData = {
        salonId,
        staffId: formik.values.staff,
        services: formik.values.services,
        customerName: formik.values.name,
        customerEmail: formik.values.email,
        customerPhone: formik.values.phone,
        date: dateString,
        startTime: selectedTimeSlot.time,
        notes: formik.values.notes,
        verificationCode // Doğrulama kodunu ekle
      };
      
      // Randevu oluştur
      setSubmitting(true);
      const response = await apiService.createAppointment(appointmentData);
      
      if (response.success) {
        // Başarılı
        console.log('Randevu başarıyla oluşturuldu:', response);
        setSubmitted(true);
        setIsVerificationModalOpen(false);
        // Sayfa en üste scroll et
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Hata durumu
        setVerificationError(response.message || 'Doğrulama kodunu kontrol ediniz');
      }
    } catch (error) {
      console.error('Doğrulama veya randevu oluşturma hatası:', error);
      setVerificationError(error instanceof Error ? error.message : 'Doğrulama yapılırken bir hata oluştu');
    } finally {
      setVerifying(false);
      setSubmitting(false);
    }
  };

  // Personel değiştikten SONRA tarih ve timeSlot alanlarını sıfırla 
  // (formik tanımlandıktan sonra)
  useEffect(() => {
    if (formik.values.staff && formik.values.staff !== currentStaffId) {
      handleStaffChange(formik.values.staff);
      formik.setFieldValue('date', null);
      formik.setFieldValue('timeSlot', '');
    }
  }, [formik.values.staff, currentStaffId]);

  const nextStep = () => {
    const isStepOneValid = selectedServices.length > 0 && formik.values.staff && formik.values.date && formik.values.timeSlot;
    
    if (formStep === 1 && isStepOneValid) {
      setFormStep(2);
      // Sayfa en üste scroll et
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (formStep === 1) {
      formik.setTouched({
        services: true,
        staff: true,
        date: true,
        timeSlot: true,
      });
    }
  };

  const prevStep = () => {
    setFormStep(1);
    // Sayfa en üste scroll et
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Seçilen hizmetleri listeleme için yardımcı fonksiyon
  const getSelectedServicesList = () => {
    return selectedServices.map(serviceId => 
      services.find(service => service.id === serviceId)
    ).filter(Boolean) as Service[];
  };

  const selectedTimeSlot = timeSlots.find((slot: TimeSlot) => slot.id === formik.values.timeSlot);
  const selectedStaff = staff.find((staffMember: TeamMember) => staffMember.id === formik.values.staff);

  if (submitted) {
    const selectedServiceNames = getSelectedServicesList()
      .map(service => service.name)
      .join(", ");
    
    return (
      <FormContainer>
        <SuccessMessage>
          <SuccessIcon className="material-icons">check_circle</SuccessIcon>
          <h2>Randevunuz Başarıyla Oluşturuldu!</h2>
          
          <RandevuInfo>
            <p>
              Sayın <strong>{formik.values.name}</strong>,
            </p>
            
            <p>
              <strong>{selectedServiceNames}</strong> hizmetleri için randevunuz<br/>
              <strong>{selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: tr })}</strong> tarihinde <strong>saat {selectedTimeSlot?.time}</strong> için kaydedilmiştir.
            </p>
            
            <p>
              Size hizmet verecek uzmanımız: <strong>{selectedStaff?.name}</strong>
            </p>
          </RandevuInfo>
          
          <InfoMessage>
            <InfoIcon className="material-icons">info</InfoIcon>
            <p>Randevu detayları SMS ile gönderilmiştir.<br/>Herhangi bir sorunuz için bizimle iletişime geçebilirsiniz.</p>
          </InfoMessage>
          
          <NewAppointmentButton onClick={() => window.location.reload()}>
            <span className="material-icons">add_circle_outline</span>
            Yeni Randevu Oluştur
          </NewAppointmentButton>
        </SuccessMessage>
      </FormContainer>
    );
  }

  if (loading) {
    return (
      <FormContainer>
        <LoadingMessage>Hizmetler yükleniyor...</LoadingMessage>
      </FormContainer>
    );
  }

  if (error) {
    return (
      <FormContainer>
        <ErrorWrapper>
          <ErrorIcon className="material-icons">error</ErrorIcon>
          <ErrorText>{error}</ErrorText>
        </ErrorWrapper>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormSteps>
        <FormStep active={formStep === 1}>
          <StepNumber active={formStep === 1}>1</StepNumber>
          <StepText>Hizmet ve Tarih Seçimi</StepText>
        </FormStep>
        <StepConnector />
        <FormStep active={formStep === 2}>
          <StepNumber active={formStep === 2}>2</StepNumber>
          <StepText>Kişisel Bilgiler</StepText>
        </FormStep>
      </FormSteps>

      <form onSubmit={formik.handleSubmit}>
        {formStep === 1 && (
          <FormContent>
            <FormSection>
              <SectionTitle>Hizmet Seçimi <HelpText>(Birden fazla hizmet seçebilirsiniz)</HelpText></SectionTitle>
              <ServiceGrid>
                {services.map((service: Service) => (
                  <ServiceOption
                    key={service.id}
                    active={selectedServices.includes(service.id)}
                    onClick={() => handleServiceToggle(service.id)}
                    type="button"
                  >
                    <ServiceName>{service.name}</ServiceName>
                    <ServiceDetails>
                      <ServicePrice>{service.price} ₺</ServicePrice>
                      <ServiceDuration>{service.duration} dk</ServiceDuration>
                    </ServiceDetails>
                    {selectedServices.includes(service.id) && 
                      <ServiceCheckmark className="material-icons">check_circle</ServiceCheckmark>
                    }
                  </ServiceOption>
                ))}
              </ServiceGrid>
              {formik.touched.services && formik.errors.services && (
                <ErrorText>{formik.errors.services as string}</ErrorText>
              )}
            </FormSection>

            {selectedServices.length > 0 && (
              <FormSection>
                <SectionTitle>Seçilen Hizmetler</SectionTitle>
                <SelectedServicesSummary>
                  <SelectedServicesList>
                    {getSelectedServicesList().map((service) => (
                      <SelectedServiceItem key={service.id}>
                        <SelectedServiceName>{service.name}</SelectedServiceName>
                        <SelectedServiceDetails>
                          <SelectedServicePrice>{service.price} ₺</SelectedServicePrice>
                          <SelectedServiceDuration>{service.duration} dk</SelectedServiceDuration>
                        </SelectedServiceDetails>
                        <RemoveServiceButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceToggle(service.id);
                          }}
                        >
                          <span className="material-icons">close</span>
                        </RemoveServiceButton>
                      </SelectedServiceItem>
                    ))}
                  </SelectedServicesList>
                  <SelectedServicesTotals>
                    <TotalItem>
                      <TotalLabel>Toplam Süre:</TotalLabel>
                      <TotalValue>{totalDuration} dakika</TotalValue>
                    </TotalItem>
                    <TotalItem>
                      <TotalLabel>Toplam Tutar:</TotalLabel>
                      <TotalValue highlight>{totalPrice} ₺</TotalValue>
                    </TotalItem>
                  </SelectedServicesTotals>
                </SelectedServicesSummary>
              </FormSection>
            )}

            {selectedServices.length > 0 && (
              <FormSection>
                <SectionTitle>Personel Seçimi</SectionTitle>
                <StaffSimpleGrid>
                  {getAvailableStaff().map((staffMember: TeamMember) => (
                    <StaffSimpleOption
                      key={staffMember.id}
                      active={formik.values.staff === staffMember.id}
                      onClick={() => formik.setFieldValue('staff', staffMember.id)}
                      type="button"
                    >
                      {staffMember.name}
                    </StaffSimpleOption>
                  ))}
                </StaffSimpleGrid>
                {formik.touched.staff && formik.errors.staff && (
                  <ErrorText>{formik.errors.staff}</ErrorText>
                )}
              </FormSection>
            )}

            {formik.values.staff && (
              <FormSection>
                <SectionTitle>Tarih Seçimi</SectionTitle>
                <DatePickerWrapper>
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    filterDate={isDateAvailable}
                    dateFormat="dd MMMM yyyy"
                    locale="tr"
                    placeholderText="Tarih seçin"
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 30)}
                    renderDayContents={renderDayContents}
                    inline
                  />
                </DatePickerWrapper>
                {formik.touched.date && formik.errors.date && (
                  <ErrorText>{formik.errors.date}</ErrorText>
                )}
              </FormSection>
            )}

            {selectedDate && (
              <FormSection>
                <SectionTitle>Saat Seçimi</SectionTitle>
                <TimeSlotGrid>
                  {timeSlots.map((slot: TimeSlot) => (
                    <TimeSlotOption
                      key={slot.id}
                      active={formik.values.timeSlot === slot.id}
                      available={slot.available}
                      disabled={!slot.available}
                      onClick={() => slot.available && formik.setFieldValue('timeSlot', slot.id)}
                      type="button"
                    >
                      {slot.time}
                    </TimeSlotOption>
                  ))}
                </TimeSlotGrid>
                {formik.touched.timeSlot && formik.errors.timeSlot && (
                  <ErrorText>{formik.errors.timeSlot}</ErrorText>
                )}
              </FormSection>
            )}

            <FormButtons>
              <NextButton type="button" onClick={nextStep}>
                Devam Et <span className="material-icons">arrow_forward</span>
              </NextButton>
            </FormButtons>
          </FormContent>
        )}

        {formStep === 2 && (
          <FormContent>
            <FormSection>
              <SectionTitle>Kişisel Bilgiler</SectionTitle>

              <FormGroup>
                <FormLabel htmlFor="name">Adınız Soyadınız</FormLabel>
                <FormInput
                  id="name"
                  name="name"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  placeholder="Adınız Soyadınız"
                />
                {formik.touched.name && formik.errors.name && (
                  <ErrorText>{formik.errors.name}</ErrorText>
                )}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="email">E-posta Adresiniz</FormLabel>
                  <FormInput
                    id="email"
                    name="email"
                    type="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    placeholder="E-posta Adresiniz"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <ErrorText>{formik.errors.email}</ErrorText>
                  )}
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="phone">Telefon Numaranız</FormLabel>
                  <FormInput
                    id="phone"
                    name="phone"
                    type="tel"
                    onChange={(e) => {
                      formik.handleChange(e);
                      // Telefon değiştiğinde doğrulama durumunu sıfırla
                      setIsPhoneVerified(false);
                      setVerificationSent(false);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone}
                    placeholder="5XX XXX XXXX"
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <ErrorText>{formik.errors.phone}</ErrorText>
                  )}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FormLabel htmlFor="notes">Eklemek İstediğiniz Notlar (Opsiyonel)</FormLabel>
                <FormTextarea
                  id="notes"
                  name="notes"
                  rows={4}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.notes}
                  placeholder="Eklemek istediğiniz notlar..."
                />
              </FormGroup>
            </FormSection>

            <FormSection>
              <SectionTitle>Randevu Özeti</SectionTitle>
              <SummaryList>
                <SummaryItem>
                  <SummaryLabel>Hizmetler:</SummaryLabel>
                  <SummaryValue highlight>
                    {getSelectedServicesList().map(service => service.name).join(", ")}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Personel:</SummaryLabel>
                  <SummaryValue highlight>{selectedStaff?.name}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Tarih:</SummaryLabel>
                  <SummaryValue>
                    {selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: tr })}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Saat:</SummaryLabel>
                  <SummaryValue>{selectedTimeSlot?.time}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Toplam Süre:</SummaryLabel>
                  <SummaryValue>{totalDuration} dakika</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Toplam Fiyat:</SummaryLabel>
                  <SummaryValue highlight>{totalPrice} ₺</SummaryValue>
                </SummaryItem>
              </SummaryList>
            </FormSection>

            <FormButtons>
              <BackButton type="button" onClick={prevStep}>
                <span className="material-icons">arrow_back</span> Geri
              </BackButton>
              <SubmitButton 
                type="submit" 
                disabled={submitting || !formik.isValid}
              >
                {submitting ? (
                  <>
                    <span className="spinner"></span> Gönderiliyor...
                  </>
                ) : (
                  'Randevu Oluştur'
                )}
              </SubmitButton>
            </FormButtons>
          </FormContent>
        )}
      </form>

      {/* SMS Doğrulama Modal */}
      {isVerificationModalOpen && (
        <VerificationModal
          phone={formik.values.phone}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          verifying={verifying}
          verificationError={verificationError}
          resendTimer={resendTimer}
          onVerify={handleVerifyCode}
          onResend={handleSendVerificationCode}
          onClose={() => setIsVerificationModalOpen(false)}
        />
      )}
    </FormContainer>
  );
};

const FormContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
`;

const FormSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
`;

const FormStep = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const StepNumber = styled.span<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-weight: 600;
  font-size: 1.2rem;
  margin-bottom: 10px;
  position: relative;
  z-index: 2;
  background: ${({ active, theme }) => 
    active 
      ? `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary})` 
      : '#f5f5f5'};
  color: ${({ active }) => active ? 'white' : '#777'};
  box-shadow: ${({ active }) => 
    active ? '0 10px 20px rgba(0, 157, 255, 0.2)' : 'none'};
  transition: all 0.3s ease;
`;

const StepText = styled.span`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
`;

const StepConnector = styled.div`
  width: 80px;
  height: 2px;
  background-color: #f0f0f0;
  margin: 0 10px;
`;

const FormContent = styled.div`
  margin-bottom: 30px;
`;

const FormSection = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text};
`;

const HelpText = styled.span`
  font-size: 0.85rem;
  margin-left: 10px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.lightText};
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 10px;
`;

const ServiceOption = styled.button<{ active: boolean }>`
  padding: 15px;
  background-color: ${({ active, theme }) => 
    active ? 'rgba(0, 157, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${({ active, theme }) => 
    active ? theme.colors.primary : '#e4e4e4'};
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${({ active, theme }) => 
      active ? 'rgba(0, 157, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)'};
    transform: translateY(-2px);
  }
`;

const ServiceName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 5px;
  color: ${({ theme }) => theme.colors.text};
`;

const ServiceDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const ServicePrice = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: 1.1rem;
`;

const ServiceDuration = styled.span`
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: "⏱";
    margin-right: 5px;
  }
`;

const ServiceCheckmark = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.3rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  margin-bottom: 8px;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FormInput = styled.input`
  padding: 12px 15px;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  background-color: white;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 157, 255, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px 15px;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  background-color: white;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 157, 255, 0.2);
  }
`;

const FormSelect = styled.select`
  padding: 12px 15px;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  background-color: white;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 157, 255, 0.2);
  }
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker {
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-family: inherit;
  }
  
  .react-datepicker__header {
    background-color: ${({ theme }) => theme.colors.primary};
    border-bottom: none;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding-top: 10px;
  }
  
  .react-datepicker__month-container {
    background-color: white;
  }
  
  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
  }
  
  .react-datepicker__day:hover {
    background-color: rgba(0, 157, 255, 0.2);
    border-radius: 50%;
  }
  
  .react-datepicker__day--disabled {
    color: #ccc;
  }
  
  .react-datepicker__current-month, 
  .react-datepicker__day-name {
    color: white;
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 10px;
  margin-top: 20px;
  max-height: 300px;
  overflow-y: auto;
`;

const TimeSlotOption = styled.button<{ active: boolean; available: boolean }>`
  padding: 10px;
  border-radius: 10px;
  text-align: center;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  position: relative;
  
  /* Boş slot */
  background-color: ${({ available, theme }) => 
    available ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 59, 48, 0.1)'};
  
  /* Dolu/Meşgul slot */
  border: 1px solid ${({ available, theme }) => 
    available ? theme.colors.border : '#ff3b30'};
  
  /* Seçili slot */
  ${({ active, theme }) => active && `
    background-color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: white;
    transform: scale(1.05);
  `}
  
  /* Meşgul/Müsait Değil */
  color: ${({ available, active, theme }) => 
    !available 
      ? '#ff3b30' 
      : active 
        ? 'white' 
        : theme.colors.text};
  
  opacity: ${({ available }) => available ? 1 : 0.8};
  cursor: ${({ available }) => available ? 'pointer' : 'not-allowed'};
  
  &:hover {
    background-color: ${({ available, active, theme }) => 
      !available 
        ? 'rgba(255, 59, 48, 0.15)' 
        : active 
          ? theme.colors.primary 
          : 'rgba(255, 255, 255, 0.1)'};
  }
  
  /* Randevulu olmadığını daha belirgin göstermek için arka planı daha belirgin yap */
  ${({ available }) => !available && `
    font-weight: 600;
    box-shadow: 0 0 4px rgba(255, 59, 48, 0.3);
  `}
`;

const FormButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 1px solid #e0e0e0;
  background-color: white;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #f9f9f9;
  }
  
  .material-icons {
    font-size: 18px;
  }
`;

const NextButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 25px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  .material-icons {
    font-size: 18px;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 25px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  .spinner {
    width: 18px;
    height: 18px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }
`;

const SummaryList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  background-color: rgba(0, 157, 255, 0.05);
  border-radius: 10px;
  padding: 15px;
`;

const SummaryItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const SummaryLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.lightText};
`;

const SummaryValue = styled.span<{ highlight?: boolean }>`
  font-weight: ${({ highlight }) => highlight ? '600' : '400'};
  color: ${({ highlight, theme }) => highlight ? theme.colors.primary : theme.colors.text};
  text-align: right;
`;

const ErrorText = styled.div`
  color: #ff3b30;
  font-size: 0.85rem;
  margin-top: 5px;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px;
  background-color: rgba(0, 157, 255, 0.05);
  border-radius: 10px;
  
  h2 {
    color: ${({ theme }) => theme.colors.primary};
    margin: 20px 0;
  }
  
  p {
    margin: 15px 0;
    line-height: 1.5;
  }
`;

const SuccessIcon = styled.span`
  font-size: 60px;
  color: #4cd964;
`;

const RandevuInfo = styled.div`
  margin: 30px 0;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  text-align: left;
  
  p {
    margin: 15px 0;
    
    &:first-of-type {
      font-size: 1.2rem;
    }
    
    strong {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const InfoMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 25px auto;
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  max-width: 80%;
  
  p {
    margin: 0;
    font-size: 0.9rem;
    color: #666;
    text-align: left;
  }
`;

const InfoIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
  margin-right: 10px;
`;

const NewAppointmentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 30px auto 0;
  padding: 12px 25px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border: none;
  border-radius: 50px;
  color: white;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 10px rgba(0, 157, 255, 0.2);
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 157, 255, 0.3);
  }
  
  .material-icons {
    font-size: 20px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.1rem;
`;

const ErrorWrapper = styled.div`
  text-align: center;
  padding: 40px;
  background-color: rgba(255, 59, 48, 0.05);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorIcon = styled.span`
  font-size: 60px;
  color: #ff3b30;
  margin-bottom: 20px;
`;

const StaffSimpleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StaffSimpleOption = styled.button<{ active: boolean }>`
  padding: 15px;
  background-color: ${({ active }) => (active ? 'white' : '#f9f9f9')};
  border: ${({ active, theme }) => 
    active ? `2px solid ${theme.colors.primary}` : '2px solid transparent'};
  border-radius: 15px;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
  overflow: hidden;
  box-shadow: ${({ active }) => 
    active ? '0 10px 30px rgba(0, 157, 255, 0.15)' : '0 5px 15px rgba(0, 0, 0, 0.03)'};
  transform: ${({ active }) => active ? 'translateY(-3px)' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.text};
  font-family: 'Poppins', sans-serif;
  height: 50px;
    
  &:hover {
    background-color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
  border-radius: 50%;
    background-color: ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
    left: 10px;
    opacity: ${({ active }) => active ? 1 : 0};
    transition: opacity 0.3s ease;
  }
  
  ${({ active }) => active && `
    &:after {
      content: '✓';
      position: absolute;
      right: 10px;
      font-size: 14px;
    }
  `}
`;

const SelectedServicesSummary = styled.div`
  background-color: #f8f8f8;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;

const SelectedServicesList = styled.div`
  margin-bottom: 20px;
`;

const SelectedServiceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: white;
  border-radius: 10px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  position: relative;
`;

const SelectedServiceName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const SelectedServiceDetails = styled.div`
  display: flex;
  align-items: center;
  margin-right: 40px;
`;

const SelectedServicePrice = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 15px;
`;

const SelectedServiceDuration = styled.span`
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 0.9rem;
  
  &:before {
    content: '⏱️';
    margin-right: 5px;
  }
`;

const RemoveServiceButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 0, 0, 0.1);
  }
  
  span {
    font-size: 18px;
  }
`;

const SelectedServicesTotals = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 15px;
`;

const TotalItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px dashed #eee;
  }
`;

const TotalLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const TotalValue = styled.span<{ highlight?: boolean }>`
  font-weight: ${({ highlight }) => highlight ? '700' : '500'};
  color: ${({ highlight, theme }) => highlight ? theme.colors.primary : theme.colors.text};
  font-size: ${({ highlight }) => highlight ? '1.1rem' : '1rem'};
`;

// Yeni styled bileşenleri
const PhoneInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const VerificationButton = styled.button`
  position: absolute;
  right: 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: ${({ theme }) => `${theme.colors.secondary}`};
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const VerifiedBadge = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  color: #4cd964;
  font-size: 0.85rem;
  
  span {
    margin-right: 4px;
    font-size: 16px;
  }
`;

const VerificationCodeSection = styled.div`
  background-color: rgba(0, 157, 255, 0.05);
  padding: 15px;
  border-radius: 8px;
  margin: 15px 0;
`;

const ModalVerificationInputContainer = styled.div`
  margin: 20px 0;
`;

const VerificationInput = styled.input`
  width: 100%;
  padding: 15px;
  font-size: 1.5rem;
  letter-spacing: 10px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 10px;
  background-color: #f9f9f9;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 157, 255, 0.2);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  border-top: 1px solid #eee;
`;

const ResendButton = styled.button`
  padding: 10px 15px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  
  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VerifyButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 1s linear infinite;
  }
`;

// Modal stiller
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: modalFadeIn 0.3s ease;
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  
  .material-icons {
    font-size: 24px;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalText = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text};
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.colors.lightText};
  font-size: 0.85rem;
  margin-top: 8px;
`;

const VerificationWarning = styled.div`
  background-color: rgba(0, 157, 255, 0.05);
  padding: 15px;
  border-radius: 8px;
  margin: 15px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .material-icons {
    margin-right: 8px;
    font-size: 20px;
  }
`;

// SMS Doğrulama Modal Bileşeni
interface VerificationModalProps {
  phone: string;
  verificationCode: string;
  setVerificationCode: (code: string) => void;
  verifying: boolean;
  verificationError: string | null;
  resendTimer: number;
  onVerify: () => void;
  onResend: () => void;
  onClose: () => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  phone,
  verificationCode,
  setVerificationCode,
  verifying,
  verificationError,
  resendTimer,
  onVerify,
  onResend,
  onClose
}) => {
  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Telefon Doğrulama</ModalTitle>
          <CloseButton onClick={onClose}>
            <span className="material-icons">close</span>
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <ModalText>
            <strong>{phone}</strong> numaralı telefonunuza gönderilen 6 haneli doğrulama kodunu giriniz.
          </ModalText>
          
          <ModalVerificationInputContainer>
            <VerificationInput
              type="text"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="• • • • • •"
              disabled={verifying}
            />
          </ModalVerificationInputContainer>
          
          {verificationError && (
            <ErrorText>{verificationError}</ErrorText>
          )}
          
          <InfoText>
            Doğrulama kodu 5 dakika geçerlidir. Doğrulama kodunu girip "Onayla" butonuna tıklayarak randevunuzu oluşturabilirsiniz.
          </InfoText>
        </ModalBody>
        
        <ModalFooter>
          <ResendButton 
            onClick={onResend} 
            disabled={resendTimer > 0 || verifying}
          >
            {resendTimer > 0 ? `Tekrar Gönder (${resendTimer}s)` : "Tekrar Gönder"}
          </ResendButton>
          
          <VerifyButton 
            onClick={onVerify}
            disabled={verifying || !verificationCode || verificationCode.length < 6}
          >
            {verifying ? (
              <>
                <span className="spinner"></span> Doğrulanıyor...
              </>
            ) : (
              "Onayla"
            )}
          </VerifyButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AppointmentForm; 